import Vue from "vue";
import App from "./App.vue";
import VeeValidate from "vee-validate";
import router from "./router";
import store from "./store";
import {
  BootstrapVue,
  IconsPlugin,
  LayoutPlugin,
  FormCheckboxPlugin,
  ToastPlugin
} from "bootstrap-vue";
import { SidebarPlugin } from "./plugins/index";
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-vue/dist/bootstrap-vue.css';
import "vue-select/dist/vue-select.css";
import moment from "moment";
import vSelect from "vue-select";
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import VueMask from 'vue-the-mask';
import datePicker from 'vue-bootstrap-datetimepicker';
import 'bootstrap/dist/css/bootstrap.css';
import 'pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css';

declare const __APP_BUILD_VERSION__: string | undefined;

const getCurrentBuildVersion = (): string => {
  if (typeof __APP_BUILD_VERSION__ === "string" && __APP_BUILD_VERSION__.trim().length > 0) {
    return __APP_BUILD_VERSION__;
  }
  const meta = document.querySelector('meta[name="app-build-version"]') as HTMLMetaElement | null;
  return meta?.content || "dev";
};

const extractBuildVersion = (html: string): string | null => {
  const match = html.match(/<meta\s+name=["']app-build-version["']\s+content=["']([^"']+)["']/i);
  return match && match[1] ? match[1] : null;
};

const startBuildVersionPolling = (): void => {
  if (process.env.NODE_ENV !== "production") {
    return;
  }

  const currentVersion = getCurrentBuildVersion();
  const check = async () => {
    try {
      const response = await fetch(`/index.html?v=${Date.now()}`, {
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache"
        }
      });
      if (!response.ok) {
        return;
      }
      const latestHtml = await response.text();
      const latestVersion = extractBuildVersion(latestHtml);
      if (latestVersion && latestVersion !== currentVersion) {
        window.location.reload();
      }
    } catch {
      // Ignore polling errors; users should not be blocked by transient network issues.
    }
  };

  window.setTimeout(() => {
    check();
    window.setInterval(check, 60000);
  }, 15000);
};

// Defensive cache fix for QA/production: remove old service workers that can pin stale app shells.
if (typeof window !== "undefined" && "serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(reg => {
      reg.unregister();
    });
  }).catch(() => {
    // ignore errors; app should continue normally
  });
}

Vue.config.productionTip = false;
Vue.prototype.moment = moment;

Vue.use(IconsPlugin);
Vue.use(BootstrapVue);
Vue.use(LayoutPlugin);
Vue.use(FormCheckboxPlugin);
Vue.use(Loading);
Vue.use(VueMask);
Vue.component('multiselect', Multiselect);
Vue.component("v-select", vSelect);
Vue.use(ToastPlugin);
Vue.use(VeeValidate, {
  inject: true,
  fieldsBagName: "veeFields",
  errorBagName: "veeErrors",
  events: 'change|blur'
});
Vue.use(SidebarPlugin, { store });
Vue.use(datePicker);

VeeValidate.Validator.extend('verify_password', {
  getMessage: field => `The password must contain at least: 1 uppercase letter, 1 lowercase letter, 1 number, and one special character`,
  validate: value => {
    const strongRegex = new RegExp("^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})");
    return strongRegex.test(value);
  }
});

VeeValidate.Validator.extend('verify_phone', {
  getMessage: field => `The ${field} must be a valid phone number with 10 digits.`,
  validate: value => {
    const cleanedValue = value.replace(/\D/g, '');
    const numeric10DigitsRegex = /^\d{10}$/;
    return numeric10DigitsRegex.test(cleanedValue);
  }
});

VeeValidate.Validator.extend('notZero', {
  getMessage: field => `${field} must be selected.`,
  validate: value => value !== 0
});

Vue.component("loading", Loading);

new Vue({
  router,
  store,
  render: (h) => h(App),
}).$mount("#app");

startBuildVersionPolling();
